import { gql } from '@apollo/client';
import { Box, Button, Center, useDisclosure } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { PropsWithChildren, useState } from 'react';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import {
  CreateConfigurationModal,
  CreateType,
} from '~components/configurations/CreateConfigurationModal';
import { CopyIcon } from '~components/ui/icons';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { ConfigurationTable_ConfigurationFragment } from '../__generated__/ConfigurationsTable.graphql';

export function DuplicateCell({
  value,
}: PropsWithChildren<
  CellProps<ConfigurationTable_ConfigurationFragment, ConfigurationTable_ConfigurationFragment>
>) {
  const { verifyUserPermissions } = useAuth();
  const hasConfigurationCreatePermission = verifyUserPermissions([Permission.ConfigurationCreate]);
  const createConfigurationModal = useDisclosure();
  const [configurationCloneValues, setConfigurationCloneValues] = useState({
    id: value.id,
    name: value.name,
    description: value.description,
    state: value.state,
  });

  const handleDraftStatusChange = (status: ConfigurationStatus) => {
    if (status === ConfigurationStatus.Published) {
      setConfigurationCloneValues({
        id: value.id,
        name: value.name,
        description: value.description,
        state: status,
      });
    }
    if (status === ConfigurationStatus.Draft && value.draft?.id) {
      setConfigurationCloneValues({
        id: value.draft.id,
        name: value.draft.name,
        description: value.draft.description,
        state: status,
      });
    }
  };
  return (
    <>
      <Box
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        fontWeight="semibold"
        width={'100%'}
      >
        <Center>
          <Button
            variant={'ghost'}
            p={0}
            color="blue.500"
            onClick={(e) => {
              createConfigurationModal.onOpen();
              e.stopPropagation();
            }}
            isDisabled={!hasConfigurationCreatePermission}
          >
            <CopyIcon boxSize={6} m={0} />
          </Button>
        </Center>
      </Box>
      <CreateConfigurationModal
        configurationId={configurationCloneValues.id}
        isOpen={createConfigurationModal.isOpen}
        onCancel={createConfigurationModal.onClose}
        createType={CreateType.Clone}
        customerId={value.customerId}
        defaultName={configurationCloneValues.name + '_copy'}
        defaultDescription={configurationCloneValues.description ?? undefined}
        configurationState={value.state ?? undefined}
        onDraftStatusSelect={value.draft?.id ? handleDraftStatusChange : undefined}
      />
    </>
  );
}

DuplicateCell.graphql = {
  mutations: {
    DuplicateDisplayConfiguration: gql`
      mutation DuplicateDisplayConfiguration(
        $configurationId: String!
        $name: String!
        $description: String
        $customerId: String!
      ) {
        cloneDisplayConfiguration(
          configurationId: $configurationId
          name: $name
          description: $description
          customerId: $customerId
        ) {
          configuration {
            ...ConfigurationTable_configuration
          }
        }
      }
    `,
  },
};
